import { useState } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import { PAGE_ROOT } from '../components/work/page-chrome';
import { isAuthSession, setAuthSession } from '../lib/api';
import { postLoginPath } from '../lib/project-path';
import { useSession } from '../components/session-context';
import PulseLogo from '../components/PulseLogo';

function googleErrorMessage(code: string | null): string | null {
  if (code === 'google') {
    return 'Use a verified @bright.blue Google account.';
  }
  if (code === 'google_unconfigured') {
    return 'Google sign-in is not configured on this server.';
  }
  return null;
}

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { ready, email: sessionEmail, starredProjectId, reload } = useSession();
  const googleError = googleErrorMessage(searchParams.get('error'));

  if (ready && sessionEmail) {
    return <Navigate to={postLoginPath(starredProjectId)} replace />;
  }

  async function loginSession(e: React.FormEvent) {
    e.preventDefault();
    if (submitting) return;
    setMessage(null);
    setSubmitting(true);
    try {
      const credentials = JSON.stringify({ email, password });
      const sessionRes = await fetch('/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: credentials,
      });
      const jwtRes = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: credentials,
      });
      if (!sessionRes.ok || !jwtRes.ok) throw new Error('Invalid credentials');
      await sessionRes.json();
      const jwtBody: unknown = await jwtRes.json();
      if (!isAuthSession(jwtBody)) {
        throw new Error('Login did not return session tokens');
      }
      setAuthSession(jwtBody);
      const session = await reload();
      navigate(postLoginPath(session.starredProjectId));
    } catch (err: unknown) {
      setMessage(err instanceof Error ? err.message : 'Login failed');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className={PAGE_ROOT}>
      <main className="flex justify-center px-4 py-16">
        <Card className="w-full max-w-sm">
          <CardHeader className="items-center text-center">
            <PulseLogo size="md" />
            <CardTitle>Sign in</CardTitle>
            <CardDescription>Use your Bright.blue account to open Pulse.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {googleError ? (
              <Alert variant="destructive">
                <AlertDescription>{googleError}</AlertDescription>
              </Alert>
            ) : null}
            <Button asChild className="w-full">
              <a href="/auth/google">Continue with Google</a>
            </Button>
            <div className="relative py-1">
              <div className="absolute inset-0 flex items-center" aria-hidden>
                <div className="w-full border-t" />
              </div>
              <p className="relative mx-auto w-fit bg-card px-2 text-xs text-muted-foreground">
                or password
              </p>
            </div>
            <form className="space-y-4" onSubmit={loginSession}>
              <div className="grid gap-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="username"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>
              {message ? (
                <Alert variant="destructive">
                  <AlertDescription>{message}</AlertDescription>
                </Alert>
              ) : null}
              <Button type="submit" className="w-full" disabled={submitting}>
                Login
              </Button>
            </form>
          </CardContent>
          <CardFooter className="justify-end">
            <Button asChild variant="link" className="h-auto p-0">
              <Link to="/forgot">Forgot password?</Link>
            </Button>
          </CardFooter>
        </Card>
      </main>
    </div>
  );
}
